// src/AuthContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { auth } from "@/lib/firebase";
import {
  onAuthStateChanged,
  signOut,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { fcmService } from "@/services/FCMService";

const Ctx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsub = () => {};
    (async () => {
      try {
        // Use local persistence to maintain login across browser sessions
        await setPersistence(auth, browserLocalPersistence);
      } catch {
        // Fallback to session persistence if local fails
        await setPersistence(auth, browserSessionPersistence);
      }
      
      // Initialize FCM service
      try {
        await fcmService.initialize();
      } catch (error) {
        console.warn('FCM initialization failed:', error);
      }
      
      unsub = onAuthStateChanged(auth, (u) => {
        setUser(u);
        setLoading(false);
      });
    })();
    return () => unsub();
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      signout: async () => {
        try {
          // Clear any cached auth state
          await signOut(auth);
          // Force clear user state immediately as a fallback
          setUser(null);
        } catch (error) {
          // Even if signOut fails, clear the user state
          setUser(null);
        }
      },
      // FCM methods
      getFCMToken: () => fcmService.getCurrentToken(),
      initializeFCM: () => fcmService.initialize(),
      // FCM Topics methods
      subscribeToTopic: (topic) => fcmService.subscribeToTopic(topic),
      unsubscribeFromTopic: (topic) => fcmService.unsubscribeFromTopic(topic),
      subscribeToTopics: (topics) => fcmService.subscribeToTopics(topics),
      getSubscribedTopics: () => fcmService.getSubscribedTopics(),
      subscribeToUserRoleTopics: (roles) => fcmService.subscribeToUserRoleTopics(roles),
    }),
    [user, loading]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
